export const NOTIFICATIONS_FETCH_INTERVAL = 1000 * 60 * 5; // 5 minutes

// https://api.app-staging.klue.com/curator-chatbot/docs
export const HATCH_CHATBOT_ENDPOINT =
  import.meta.env.VITE_CURATOR_CHATBOT_BASE_URL + '/hatch/ask-stream';

export const HATCH_CHATBOT_THREADS_ENDPOINT =
  import.meta.env.VITE_CURATOR_CHATBOT_BASE_URL + '/hatch/threads/';

export const HATCH_CHATBOT_THREAD_ENDPOINT =
  import.meta.env.VITE_CURATOR_CHATBOT_BASE_URL + '/hatch/threads/:threadId';

/** Curator-only seller answer preview thread (ENG-2250). */
export const HATCH_CHATBOT_PREVIEW_THREAD_ENDPOINT =
  import.meta.env.VITE_CURATOR_CHATBOT_BASE_URL +
  '/hatch/threads/preview/:threadId';

/** Company-scoped seller questions with injected Smart Answers (ENG-1873). */
export const HATCH_SELLER_QUESTIONS_ENDPOINT =
  import.meta.env.VITE_CURATOR_CHATBOT_BASE_URL + '/hatch/seller-questions/';

export const HATCH_SHARED_THREADS_ENDPOINT =
  import.meta.env.VITE_CURATOR_CHATBOT_BASE_URL + '/hatch/shared_threads/';

export const HATCH_FORK_SHARED_THREAD_ENDPOINT =
  import.meta.env.VITE_CURATOR_CHATBOT_BASE_URL +
  '/hatch/threads/shared/:sharedThreadId';

export const HATCH_CHAT_INSTRUCTIONS_ENDPOINT =
  import.meta.env.VITE_CURATOR_CHATBOT_BASE_URL +
  '/hatch/settings/instructions';

export const HATCH_SALES_METHODOLOGY_OPTIONS_ENDPOINT =
  import.meta.env.VITE_CURATOR_CHATBOT_BASE_URL +
  '/hatch/settings/instructions/sales-methodology-options';

export const DSQ_FEEDBACK_CHAT_ENDPOINT =
  import.meta.env.VITE_CURATOR_CHATBOT_BASE_URL + '/dsq-feedback/chat/';

export const DSQ_REVIEW_CHAT_ENDPOINT =
  import.meta.env.VITE_CURATOR_CHATBOT_BASE_URL + '/dsq-review/chat/';

export const RESEARCH_MODE_AGUI_ENDPOINT =
  import.meta.env.VITE_CURATOR_CHATBOT_BASE_URL + '/research/chat/';

export const RESEARCH_MODE_THREADS_ENDPOINT =
  import.meta.env.VITE_CURATOR_CHATBOT_BASE_URL + '/research/threads/';

export const RESEARCH_MODE_THREAD_ENDPOINT =
  RESEARCH_MODE_THREADS_ENDPOINT + ':threadId/';

export const RESEARCH_MODE_SOURCES_ENDPOINT =
  import.meta.env.VITE_CURATOR_CHATBOT_BASE_URL + '/research/sources/';

export const ANSWER_MODE_AGUI_ENDPOINT =
  import.meta.env.VITE_CURATOR_CHATBOT_BASE_URL + '/cc/answer/chat/';

export const ANSWER_MODE_THREADS_ENDPOINT =
  import.meta.env.VITE_CURATOR_CHATBOT_BASE_URL + '/cc/answer/threads/';

export const ANSWER_MODE_THREAD_ENDPOINT =
  ANSWER_MODE_THREADS_ENDPOINT + ':threadId/';

export const ANSWER_MODE_SOURCES_ENDPOINT =
  import.meta.env.VITE_CURATOR_CHATBOT_BASE_URL + '/cc/answer/sources/';

export const EXPORT_ENDPOINT =
  import.meta.env.VITE_CURATOR_CHATBOT_BASE_URL + '/export/';

// Fallback matters: local dev reads /stak from .env, but .env is gitignored —
// CI and fresh checkouts have none, and an undefined base turns every stak
// endpoint into "/api/undefined/..." (and its MSW mock pattern into
// "/apiundefined/...", which matches nothing).
const STAK_BASE_URL = (import.meta.env.VITE_STAK_BASE_URL || '/stak').replace(
  /\/$/,
  '',
);

// The Customer Proof consent plugin is mounted at the API host root beside
// stak, not under it: deriving this from STAK_BASE_URL would build a path that
// does not exist. Same fallback reasoning as STAK_BASE_URL above.
const CUSTOMER_PROOF_BASE_URL = (
  import.meta.env.VITE_CUSTOMER_PROOF_BASE_URL || '/customer-proof'
).replace(/\/$/, '');

export const CUSTOMER_PROOF_ENDPOINT = `${CUSTOMER_PROOF_BASE_URL}/v0`;

export const HATCH_ARTIFACTS_ENDPOINT = `${STAK_BASE_URL}/v0/artifacts/`;

// Block-shaped artifact endpoint (ENG-627). When `isAgentBlockArtifactsEnabled`
// is on, the renderer fetches resolved blocks here instead of using the
// legacy adapter on top of HATCH_ARTIFACTS_ENDPOINT.
export const HATCH_BLOCK_ARTIFACTS_ENDPOINT = `${STAK_BASE_URL}/v0/block-artifacts/`;

// Curator "add a story from a URL" (ENG-3528 / ENG-3930): the sync lookup
// behind the "we already have this story" card. The job that fetches, drafts
// and attaches lives under `${HATCH_BLOCK_ARTIFACTS_ENDPOINT}{digest}/references/from-url/`
// (see modules/hatch/api/news-items.api.ts).
export const HATCH_NEWS_ITEMS_BY_URL_ENDPOINT = `${HATCH_BLOCK_ARTIFACTS_ENDPOINT}news-items/by-url/`;

// Home feed (ENG-2218). Cross-type "latest artifacts" over the last 7 days
// across the Klue-defined type set, newest-first. Klue-scoped server-side; no
// per-user filters in M1. Replaces the home daily-brief.
export const HATCH_BLOCK_ARTIFACTS_FEED_ENDPOINT = `${STAK_BASE_URL}/v0/block-artifacts/feed/`;

// Per-(company, artifact_type) email delivery settings (ENG-3120/3121):
// the digest sheet's Delivery popover reads/writes these, and the
// recipients sub-path returns honest audience counts backed by
// notification-service's dry run.
export const HATCH_DELIVERY_SETTINGS_ENDPOINT =
  import.meta.env.VITE_STAK_BASE_URL + '/v0/block-artifacts/types/';
export const HATCH_DELIVERY_SETTINGS_TYPE_ENDPOINT = `${HATCH_DELIVERY_SETTINGS_ENDPOINT}:artifactType/delivery/`;
export const HATCH_DELIVERY_SETTINGS_RECIPIENT_PREVIEW_ENDPOINT = `${HATCH_DELIVERY_SETTINGS_TYPE_ENDPOINT}recipients/`;

// Schema rows for block artifacts (stak#1685). Powers the chip-based
// filter bar: filterable attributes, enum_values, types.
export const HATCH_BLOCK_ARTIFACT_SCHEMAS_ENDPOINT = `${STAK_BASE_URL}/v0/block-artifact-schemas`; // TODO Backend should have a consistent trailing slash policy

// DSQ (Deal Support Questions) endpoints
const DSQ_BASE_URL = (import.meta.env.VITE_DSQ_BASE_URL || '/dsq').replace(
  /\/$/,
  '',
);

export const DSQ_BATTLECARD_PROMPTS_ENDPOINT = `${DSQ_BASE_URL}/v0/battlecard-prompts/`;

export const DSQ_CANONICAL_QUESTIONS_ENDPOINT = `${DSQ_BASE_URL}/v0/canonical-questions/`;

/** Company-scoped questions with derived generation status (ENG-2104). Filter via `origins`. */
export const DSQ_CANONICAL_QUESTION_GENERATION_STATUS_ENDPOINT = `${DSQ_CANONICAL_QUESTIONS_ENDPOINT}generation-status/`;

export const DSQ_CANONICAL_QUESTION_ENDPOINT = `${DSQ_CANONICAL_QUESTIONS_ENDPOINT}:questionId/`;

export const DSQ_CANONICAL_QUESTION_ANSWERS_ENDPOINT = `${DSQ_CANONICAL_QUESTION_ENDPOINT}answers/`;

export const DSQ_CANONICAL_QUESTION_LATEST_ANSWER_ENDPOINT = `${DSQ_CANONICAL_QUESTION_ANSWERS_ENDPOINT}latest/`;

export const DSQ_CANONICAL_QUESTION_LATEST_ANSWER_PREVIEW_ENDPOINT = `${DSQ_CANONICAL_QUESTION_LATEST_ANSWER_ENDPOINT}preview/`;

export const DSQ_CANONICAL_QUESTION_ANSWER_PREVIEW_ENDPOINT = `${DSQ_CANONICAL_QUESTION_ANSWERS_ENDPOINT}:versionId/preview/`;

export const DSQ_GENERATE_ANSWER_ENDPOINT = `${DSQ_CANONICAL_QUESTION_ENDPOINT}generate-answer/`;

export const DSQ_GENERATION_STATUS_ENDPOINT = `${DSQ_GENERATE_ANSWER_ENDPOINT}:generationId/status/`;

export const DSQ_REGENERATE_WITH_FEEDBACK_ENDPOINT = `${DSQ_CANONICAL_QUESTION_ENDPOINT}regenerate-with-feedback/`;

export const DSQ_RESTORE_ANSWER_VERSION_ENDPOINT = `${DSQ_CANONICAL_QUESTION_ANSWERS_ENDPOINT}:versionId/restore/`;

export const DSQ_ANSWER_REVIEWS_ENDPOINT = `${DSQ_CANONICAL_QUESTION_ANSWERS_ENDPOINT}:versionId/reviews/`;

export const DSQ_SUGGESTED_PROMPTS_ENDPOINT = `${DSQ_BASE_URL}/v0/suggested-prompts/`;

export const DSQ_SEMANTIC_SEARCH_ENDPOINT = `${DSQ_CANONICAL_QUESTIONS_ENDPOINT}semantic-search/`;

/** Proposed Smart Answer updates awaiting review — the content updates inbox (stak#2397). */
export const DSQ_UPDATES_ENDPOINT = `${DSQ_BASE_URL}/v0/updates/`;

export const DSQ_UPDATE_ENDPOINT = `${DSQ_UPDATES_ENDPOINT}:updateId/`;

export const DSQ_UPDATE_DECIDE_ENDPOINT = `${DSQ_UPDATE_ENDPOINT}decide/`;

/** An update's stored revisions, oldest first. */
export const DSQ_UPDATE_REVISIONS_ENDPOINT = `${DSQ_UPDATE_ENDPOINT}revisions/`;

/** DELETE cancels the update's background regeneration. */
export const DSQ_UPDATE_REGENERATE_ENDPOINT = `${DSQ_UPDATE_ENDPOINT}regenerate-with-feedback/`;

/** Org units (companies and products) whose answer updates wait in the inbox (stak#2452). */
export const DSQ_REVIEW_SETTINGS_ORG_UNITS_ENDPOINT = `${DSQ_BASE_URL}/v0/review-settings/org-units/`;

export const DSQ_REVIEW_SETTINGS_ORG_UNITS_REMOVE_ENDPOINT = `${DSQ_REVIEW_SETTINGS_ORG_UNITS_ENDPOINT}remove/`;

/** Admins and curators who can be assigned to review (stak#2476). */
export const DSQ_REVIEW_SETTINGS_REVIEWERS_ENDPOINT = `${DSQ_BASE_URL}/v0/review-settings/reviewers/`;

/** Who reviews each org unit; none means any admin (stak#2476). */
export const DSQ_REVIEW_SETTINGS_ASSIGNMENTS_ENDPOINT = `${DSQ_BASE_URL}/v0/review-settings/assignments/`;

export const DSQ_REVIEW_SETTINGS_ASSIGNMENTS_REMOVE_ENDPOINT = `${DSQ_REVIEW_SETTINGS_ASSIGNMENTS_ENDPOINT}remove/`;

// Deal Tips - https://api.app.klue.com/deals/docs
// Fallback to '/deals' when VITE_DEALS_BASE_URL is unset (matches DSQ pattern).
// Without the fallback the endpoint becomes "undefined/v0/deal-tips/" in
// environments that haven't set the env var (e.g. Storybook), which breaks
// both real requests and MSW handler matching.
const DEALS_BASE_URL = (
  import.meta.env.VITE_DEALS_BASE_URL || '/deals'
).replace(/\/$/, '');

export const DEAL_TIPS_ENDPOINT = `${DEALS_BASE_URL}/v0/deal-tips/`;

export const DEAL_TIPS_DETAIL_ENDPOINT = `${DEAL_TIPS_ENDPOINT}:id/`;

export const DEAL_TIPS_RESEND_ENDPOINT = `${DEAL_TIPS_DETAIL_ENDPOINT}resend/`;

export const DEAL_TIPS_FILTER_OPTIONS_ENDPOINT = `${DEAL_TIPS_ENDPOINT}filter-options/`;

export const HATCH_AGENT_CONTROLS_ENDPOINT = `${DEALS_BASE_URL}/v0/agent-controls/`;

export const HATCH_AGENT_CONTROLS_FIELD_OPTIONS_ENDPOINT = `${HATCH_AGENT_CONTROLS_ENDPOINT}field-options/`;

export const HATCH_AGENT_CONTROLS_AGENT_ENDPOINT = `${HATCH_AGENT_CONTROLS_ENDPOINT}:agentName/`;

// https://api.app-staging.klue.com/indexer/docs
export const HATCH_CITATIONS_ENDPOINT =
  import.meta.env.VITE_INDEXER_BASE_URL + '/:indexerType/:id/citation/';

export const HATCH_STAKS_ENDPOINT =
  import.meta.env.VITE_INDEXER_BASE_URL + '/staks/';

export const HATCH_STAK_CARD_ENDPOINT =
  import.meta.env.VITE_INDEXER_BASE_URL + '/stak-cards/';

// https://api.app-staging.klue.com/entity-graph/docs
// Fallback to '/entity-graph' when VITE_ENTITY_GRAPH_BASE_URL is unset (matches
// the DEALS/DSQ pattern). Without it the endpoints become "undefined/v1/..." in
// env-less contexts (CI unit tests, Storybook), breaking real requests and MSW
// handler matching.
const ENTITY_GRAPH_BASE_URL = (
  import.meta.env.VITE_ENTITY_GRAPH_BASE_URL || '/entity-graph'
).replace(/\/$/, '');

export const ENTITY_GRAPH_ENDPOINT = `${ENTITY_GRAPH_BASE_URL}/v1/graph`;

// The tenant's own products and their capabilities. `/v1/graph` carries a
// `solutions` key on every node but never fills it, so this is the only read
// that returns capabilities.
export const ENTITY_GRAPH_PRODUCT_MAP_ENDPOINT = `${ENTITY_GRAPH_BASE_URL}/v1/product-map`;

export const ENTITY_GRAPH_ORG_UNIT_ENDPOINT = `${ENTITY_GRAPH_BASE_URL}/v1/org-unit`;

export const ENTITY_GRAPH_RELATIONSHIP_ENDPOINT = `${ENTITY_GRAPH_BASE_URL}/v1/org-unit-relationship`;

export const ENTITY_GRAPH_DRAFT_GRAPH_ENDPOINT = `${ENTITY_GRAPH_BASE_URL}/v1/draft-graph`;

export const ENTITY_GRAPH_FINALIZE_ENDPOINT = `${ENTITY_GRAPH_BASE_URL}/v1/draft-graph/finalize`;

export const HATCH_SNIPPETS_ENDPOINT =
  import.meta.env.VITE_ATHENA_BASE_URL + '/curated-snippets';

export const ATHENA_SOURCE_SETTINGS_ENDPOINT =
  import.meta.env.VITE_ATHENA_BASE_URL + '/source-settings/';

export const HATCH_SOURCES_ENDPOINT =
  import.meta.env.VITE_CURATOR_CHATBOT_BASE_URL + '/hatch/sources/';

export const HATCH_PAIRWISE_ENDPOINT =
  import.meta.env.VITE_CURATOR_CHATBOT_BASE_URL + '/hatch/pairwise';

export const HATCH_PAIRWISE_EXPERIMENTS_ENDPOINT = `${HATCH_PAIRWISE_ENDPOINT}/experiments`;

// Metrics API — POST /metrics/v1/query
const METRICS_BASE_URL = (
  import.meta.env.VITE_METRICS_BASE_URL || '/metrics'
).replace(/\/$/, '');

export const METRICS_QUERY_ENDPOINT = `${METRICS_BASE_URL}/v1/query`;

export const CHAT_MESSAGE_FEEDBACK_ENDPOINT =
  import.meta.env.VITE_CURATOR_CHATBOT_BASE_URL +
  '/feedback/messages/:messageId';

export const CHAT_THREAD_FEEDBACK_ENDPOINT =
  import.meta.env.VITE_CURATOR_CHATBOT_BASE_URL + '/feedback/threads/:threadId';
